/**
 * EventNormalizer — converts bridge-level AgentEvent (raw driver events) to
 * skaile-agent-types AgentEvent (normalized, human-readable events).
 *
 * Stateful: tracks active subagents and dev-server detection across a session.
 * Call reset() between sessions.
 */

import type {
  AgentEvent as NormalizedEvent,
  NativeApprovalDescription,
} from "@skaile/workspaces/types";
import { classifyClaudeSdkError } from "./error-classifier.js";
import type { AgentMessage, AgentEvent as BridgeEvent, ContentBlock } from "./types.js";

/**
 * The optional usage keys a `result` event carries, in the shape both
 * `finished` and a result-sourced `error` emit them. Absent keys stay absent.
 */
function resultUsageFields(
  event: Pick<
    Extract<BridgeEvent, { type: "result" | "error" }>,
    "tokens" | "modelUsage" | "contextTokens" | "contextWindow"
  >,
) {
  return {
    ...(event.tokens ? { tokens: event.tokens } : {}),
    ...(event.modelUsage ? { modelUsage: event.modelUsage } : {}),
    ...(typeof event.contextTokens === "number" ? { contextTokens: event.contextTokens } : {}),
    ...(typeof event.contextWindow === "number" ? { contextWindow: event.contextWindow } : {}),
  };
}

/**
 * The tool a driver uses to park a turn on a human answer. Both branches that
 * recognise it must use this constant: `message_start` turns it into a
 * `question`, and `tool_call` must then stay silent about it.
 */
const QUESTION_TOOL = "AskUserQuestion";

/**
 * Converts raw bridge-level {@link BridgeEvent} payloads into normalized
 * `@skaile/workspaces/types` `AgentEvent` payloads consumed by the runner and transport.
 *
 * Stateful: tracks active subagents and dev-server detection across a session.
 * Call {@link EventNormalizer.reset} between sessions to clear accumulated state.
 *
 * @docLink packages/bridge/api-reference#normalizer
 */
export class EventNormalizer {
  private activeSubagents = new Map<string, string>();
  private devServerStartingEmitted = false;
  private devServerReadyEmitted = false;
  /** True when message_update deltas have been emitted for the current message. */
  private hasStreamedText = false;

  /**
   * Convert a single bridge event into zero or more normalized events.
   * One bridge event can produce multiple normalized events (e.g., a message_start
   * with text + tool_use blocks produces both text and tool_call events).
   */
  normalize(event: BridgeEvent): NormalizedEvent[] {
    switch (event.type) {
      case "message_start":
        return this.handleMessageStart(event.message);

      case "message_update":
        return this.handleMessageUpdate(event);

      case "message_end":
        return [];

      case "turn_end":
        return this.handleTurnEnd(event.toolResults);

      case "result":
        return this.handleResult(event);

      case "error":
        return [
          {
            type: "error",
            message: event.error,
            fatal: event.fatal ?? true,
            category: event.detail?.category,
            ...(event.detail?.hint ? { hint: event.detail.hint } : {}),
            ...resultUsageFields(event),
          },
        ];

      case "tool_call": {
        // Already handled in message_start — emit status only. The question tool
        // is the exception: the turn parks on the user's answer, so reporting
        // work here would undo the idle its `question` event just set, and
        // nothing follows to correct it while the user reads the prompt.
        const toolName = event.name ?? event.tool?.name;
        if (toolName === QUESTION_TOOL) return [];
        return [{ type: "status", phase: "working" }];
      }

      case "tool_execution_end":
        return [];

      case "agent_end":
        return [{ type: "status", phase: "idle" }];

      case "commands_available":
        // Pass through to transport — the gateway caches this event
        return [event as unknown as NormalizedEvent];

      case "session_info":
        // Pass through to transport — the forge layer stores the OMP session ID
        return [event as unknown as NormalizedEvent];

      case "ui_render":
      case "ui_render_update":
      case "ui_clear":
        // Pass through verbatim — drivers that implement the Agent UI Protocol emit these directly
        return [event as unknown as NormalizedEvent];

      default:
        return [];
    }
  }

  /** Clear all stateful tracking between sessions. */
  reset(): void {
    this.activeSubagents.clear();
    this.devServerStartingEmitted = false;
    this.devServerReadyEmitted = false;
    this.hasStreamedText = false;
  }

  // ---------------------------------------------------------------------------
  // message_start → text, tool_call, question, subagent
  // ---------------------------------------------------------------------------

  private handleMessageStart(message: AgentMessage): NormalizedEvent[] {
    // Only process assistant messages. Drivers (e.g. omp) may echo the user's
    // message as a message_start with role:"user" before streaming the response.
    // Emitting a text event for that echo would prepend the user's input to the
    // assistant bubble on the frontend.
    if (message.role !== "assistant") return [];

    const events: NormalizedEvent[] = [];
    const blocks = this.getContentBlocks(message);

    for (const block of blocks) {
      if (block.type === "text" && block.text) {
        // Skip full text if we already streamed it via message_update deltas
        if (!this.hasStreamedText) {
          events.push({ type: "text", content: block.text });
        }
      } else if (block.type === "tool_use" && block.name) {
        events.push(...this.handleToolUse(block));
      }
    }

    this.hasStreamedText = false;
    return events;
  }

  private handleToolUse(block: ContentBlock): NormalizedEvent[] {
    const name = block.name!;
    const input = (block.input ?? {}) as Record<string, unknown>;

    if (name === QUESTION_TOOL) {
      return this.handleAskUserQuestion(input);
    }
    if (name === "Agent") {
      return this.handleAgentToolUse(block, input);
    }
    return this.handleGenericToolUse(name, input);
  }

  private handleAskUserQuestion(input: Record<string, unknown>): NormalizedEvent[] {
    const events: NormalizedEvent[] = [];
    const candidate = input.approval as Partial<NativeApprovalDescription> | undefined;
    const approval =
      candidate &&
      ["command", "file", "permissions", "mcp"].includes(candidate.kind ?? "") &&
      Array.isArray(candidate.details) &&
      candidate.details.length > 0 &&
      candidate.details.length <= 64 &&
      candidate.details.every((detail) => typeof detail === "string" && detail.length <= 8192) &&
      candidate.details.reduce((total, detail) => total + detail.length, 0) <= 65536
        ? (candidate as NativeApprovalDescription)
        : undefined;
    if (Object.hasOwn(input, "approval") && !approval) return [];
    // Claude SDK sends { questions: [{ question, header, options: [{ label, description }] }] }
    // Legacy/simple format sends { question: string, options?: string[] }
    const questions = input.questions;
    if (Array.isArray(questions)) {
      for (const q of questions) {
        const qObj = q as Record<string, unknown>;
        const question = String(qObj.question ?? "");
        const rawOpts = qObj.options;
        const options = Array.isArray(rawOpts)
          ? rawOpts.map((o: any) => (typeof o === "string" ? o : String(o.label ?? o)))
          : undefined;
        events.push({
          type: "question",
          question,
          options,
          ...(typeof input.requestId === "string" ? { requestId: input.requestId } : {}),
          ...(approval ? { approval } : {}),
        });
      }
    } else {
      const question = String(input.question ?? input.text ?? "");
      const rawOptions = input.options;
      const options = Array.isArray(rawOptions) ? rawOptions.map(String) : undefined;
      events.push({
        type: "question",
        question,
        options,
        ...(typeof input.requestId === "string" ? { requestId: input.requestId } : {}),
        ...(approval ? { approval } : {}),
      });
    }
    return events;
  }

  private handleAgentToolUse(
    block: ContentBlock,
    input: Record<string, unknown>,
  ): NormalizedEvent[] {
    const agentName = String(input.subagent_type ?? input.description ?? "unknown");
    const description = String(input.description ?? input.prompt ?? "").slice(0, 200);
    if (block.id) {
      this.activeSubagents.set(block.id, agentName);
    }
    return [
      { type: "subagent", name: agentName, status: "started", description },
      { type: "status", phase: "working" },
    ];
  }

  private handleGenericToolUse(name: string, input: Record<string, unknown>): NormalizedEvent[] {
    const events: NormalizedEvent[] = [];
    events.push({
      type: "tool_call",
      tool: name,
      input,
      summary: summarizeToolCall(name, input),
    });
    events.push({ type: "status", phase: "working" });

    if (name === "Bash" && typeof input.command === "string") {
      const devEvent = this.detectDevServerStarting(input.command);
      if (devEvent) events.push(devEvent);
    }

    return events;
  }

  // ---------------------------------------------------------------------------
  // message_update → streaming text
  // ---------------------------------------------------------------------------

  private handleMessageUpdate(event: BridgeEvent): NormalizedEvent[] {
    const delta = (event as any)._textDelta as string | undefined;
    if (delta) {
      this.hasStreamedText = true;
      return [{ type: "text", content: delta }];
    }
    return [];
  }

  // ---------------------------------------------------------------------------
  // turn_end → tool_result, subagent finished, file_changed, dev_server
  // ---------------------------------------------------------------------------

  private handleTurnEnd(toolResults?: AgentMessage[]): NormalizedEvent[] {
    if (!toolResults?.length) return [];

    const events: NormalizedEvent[] = [];

    for (const result of toolResults) {
      const toolName = result.toolName ?? "unknown";
      const outputData = result.data ?? result.content;
      const output = typeof outputData === "string" ? outputData : JSON.stringify(outputData);

      // Check if this is a subagent result
      if (toolName === "Agent" || this.isSubagentResult(result)) {
        events.push(...this.handleSubagentResult(result, output));
      } else {
        events.push(...this.handleToolResult(toolName, outputData, output));
      }
    }

    return events;
  }

  private handleSubagentResult(result: AgentMessage, output: string): NormalizedEvent[] {
    const events: NormalizedEvent[] = [];
    const agentName = this.resolveSubagentName(result);
    const description = output.slice(0, 200);
    events.push({ type: "subagent", name: agentName, status: "finished", description });

    if (agentName === "dev-server") {
      const devEvent = this.detectDevServerReady(output);
      if (devEvent) events.push(devEvent);
    }
    return events;
  }

  private handleToolResult(
    toolName: string,
    outputData: unknown,
    output: string,
  ): NormalizedEvent[] {
    const events: NormalizedEvent[] = [];
    events.push({
      type: "tool_result",
      tool: toolName,
      output: outputData,
      summary: summarizeToolResult(toolName, outputData),
    });

    // Detect file changes from Write/Edit results
    const fileEvents = detectFileChanges(toolName, outputData);
    if (fileEvents.length > 0) events.push(...fileEvents);

    // Detect dev server from Bash output
    if (toolName === "Bash") {
      const devEvent = this.detectDevServerReady(output);
      if (devEvent) events.push(devEvent);
    }
    return events;
  }

  private isSubagentResult(result: AgentMessage): boolean {
    // Check if the tool result's id maps to a tracked subagent
    if (result.toolName && this.activeSubagents.has(result.toolName)) return true;
    return false;
  }

  private resolveSubagentName(result: AgentMessage): string {
    // toolName on bridge AgentMessage is resolved from toolIdToName in the driver
    const name = result.toolName ? this.activeSubagents.get(result.toolName) : undefined;
    if (name) {
      this.activeSubagents.delete(result.toolName!);
      return name;
    }
    return result.toolName ?? "unknown";
  }

  // ---------------------------------------------------------------------------
  // result → finished or error
  // ---------------------------------------------------------------------------

  private handleResult(event: BridgeEvent & { type: "result" }): NormalizedEvent[] {
    const usage = resultUsageFields(event);
    if (event.subtype === "success") {
      return [
        {
          type: "finished",
          summary: event.summary ?? "Task completed",
          costUsd: event.costUsd ?? 0,
          ...usage,
        },
      ];
    }

    const errors = event.errors ?? [];
    // Run the same classifier the claude-sdk driver uses on its `error`
    // event so the result-error path also surfaces a category and hint
    // (drivers other than claude-sdk emit `result` without a paired
    // `error`, so this is the only place they get classified).
    const detail = classifyClaudeSdkError({ errors, subtype: event.subtype });
    // The turn billed even though it failed: carry its usage so a ledger
    // that only writes on `finished` can write here too. Unlike the finished
    // branch, no `costUsd: 0` fallback — an absent figure stays absent.
    return [
      {
        type: "error",
        message: `Agent stopped: ${event.subtype}${errors.length ? ` — ${errors.join("; ")}` : ""}`,
        fatal: event.subtype === "error_during_execution",
        category: detail.category,
        ...(detail.hint ? { hint: detail.hint } : {}),
        ...(typeof event.costUsd === "number" ? { costUsd: event.costUsd } : {}),
        ...usage,
      },
    ];
  }

  // ---------------------------------------------------------------------------
  // Dev server detection
  // ---------------------------------------------------------------------------

  private detectDevServerStarting(command: string): NormalizedEvent | null {
    if (this.devServerStartingEmitted) return null;
    const cmd = command.toLowerCase();
    const isFrontendStart =
      (cmd.includes("pnpm run dev") || cmd.includes("pnpm dev") || cmd.includes("vite")) &&
      (cmd.includes("frontend") || cmd.includes("dev:preview"));
    if (isFrontendStart) {
      this.devServerStartingEmitted = true;
      return { type: "dev_server", url: "http://localhost:3000", status: "starting" };
    }
    return null;
  }

  private detectDevServerReady(text: string): NormalizedEvent | null {
    if (this.devServerReadyEmitted) return null;

    const explicitMatch = text.match(/DEVSERVER_READY:(https?:\/\/[^\s"\\]+)/);
    if (explicitMatch) {
      this.devServerReadyEmitted = true;
      return { type: "dev_server", url: explicitMatch[1]!, status: "ready" };
    }

    const viteMatch = text.match(/Local:\s+(https?:\/\/localhost:\d+)/);
    if (viteMatch) {
      this.devServerReadyEmitted = true;
      return { type: "dev_server", url: viteMatch[1]!, status: "ready" };
    }

    if (text.includes("FRONTEND_READY")) {
      this.devServerReadyEmitted = true;
      return { type: "dev_server", url: "http://localhost:3000", status: "ready" };
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private getContentBlocks(message: AgentMessage): ContentBlock[] {
    if (Array.isArray(message.content)) return message.content;
    if (typeof message.content === "string" && message.content) {
      return [{ type: "text", text: message.content }];
    }
    return [];
  }
}

// ---------------------------------------------------------------------------
// Pure helper functions (exported for reuse)
// ---------------------------------------------------------------------------

/**
 * Produce a human-readable one-line summary of a tool call for the activity log.
 *
 * Returns a string like `$ git status` for Bash, `Read src/index.ts` for Read, etc.
 * Falls back to `ToolName({...input...})` for unrecognised tools.
 *
 * @param tool - Tool name (e.g. `"Bash"`, `"Write"`, `"Grep"`).
 * @param input - Tool input record passed to the call.
 * @returns A short display string suitable for the activity feed.
 * @docLink packages/bridge/api-reference#normalizer
 */
export function summarizeToolCall(tool: string, input: Record<string, unknown>): string {
  switch (tool) {
    case "Bash":
      return `$ ${String(input.command ?? "").slice(0, 200)}`;
    case "Write":
      return `Create ${String(input.file_path)}`;
    case "Edit":
      return `Edit ${String(input.file_path)}`;
    case "Read":
      return `Read ${String(input.file_path)}`;
    case "Glob":
      return `Find ${String(input.pattern)}`;
    case "Grep":
      return `Search for ${String(input.pattern)}`;
    default:
      return `${tool}(${JSON.stringify(input).slice(0, 100)})`;
  }
}

/** Extract plain text from an Anthropic content-block array (e.g. tool results returned by Claude SDK). */
function extractContentBlockText(blocks: unknown[]): string | null {
  const textBlocks = blocks.filter(
    (b): b is { type: "text"; text: string } =>
      typeof b === "object" &&
      b !== null &&
      (b as Record<string, unknown>).type === "text" &&
      typeof (b as Record<string, unknown>).text === "string",
  );
  if (textBlocks.length === 0) return null;
  return textBlocks.map((b) => b.text).join("\n");
}

/**
 * Produce a human-readable one-line summary of a tool result for the activity log.
 *
 * Handles Bash stdout/stderr, Write/Edit file paths, ApplyPatch change counts, and
 * Anthropic content-block arrays. Falls back to `JSON.stringify` for unknown shapes.
 *
 * @param tool - Tool name.
 * @param result - Raw tool result payload.
 * @returns A short display string, truncated to 200 characters.
 * @docLink packages/bridge/api-reference#normalizer
 */
export function summarizeToolResult(tool: string, result: unknown): string {
  // Handle Anthropic content-block arrays: [{ type: "text", text: "..." }, ...]
  if (Array.isArray(result)) {
    const text = extractContentBlockText(result);
    return (text ?? JSON.stringify(result)).slice(0, 200);
  }
  if (typeof result !== "object" || !result) {
    return String(result).slice(0, 200);
  }
  const r = result as Record<string, unknown>;
  switch (tool) {
    case "Bash": {
      const stderr = String(r.stderr ?? "").trim();
      if (stderr) return `stderr: ${stderr.slice(0, 200)}`;
      return (
        String(r.stdout ?? "")
          .trim()
          .slice(0, 200) || "(no output)"
      );
    }
    case "Write":
      return `${String(r.type)}: ${String(r.filePath)}`;
    case "Edit":
      return `Edited ${String(r.filePath)}`;
    case "ApplyPatch":
      return `${Array.isArray(r.changes) ? r.changes.length : 0} file changes`;
    default:
      return JSON.stringify(result).slice(0, 200);
  }
}

/**
 * Detect file-change events from a tool result, returning zero or more `file_changed` events.
 *
 * Handles `Write` (single create/edit), `Edit` (single edit), and `ApplyPatch` (multi-file
 * patch with per-change `kind` classification). Returns an empty array for all other tools.
 *
 * @param toolName - Name of the tool that produced the result.
 * @param result - Raw tool result payload.
 * @returns Array of `file_changed` normalized events.
 * @docLink packages/bridge/api-reference#normalizer
 */
export function detectFileChanges(toolName: string, result: unknown): NormalizedEvent[] {
  if (typeof result !== "object" || !result) return [];
  const r = result as Record<string, unknown>;

  if (toolName === "Write" && r.filePath) {
    return [
      {
        type: "file_changed",
        path: String(r.filePath),
        action: r.type === "create" ? "create" : "edit",
      },
    ];
  }
  if (toolName === "Edit" && r.filePath) {
    return [{ type: "file_changed", path: String(r.filePath), action: "edit" }];
  }
  if (toolName === "ApplyPatch" && Array.isArray(r.changes)) {
    return r.changes
      .filter(
        (change): change is { path: string; kind: "add" | "delete" | "update" } =>
          typeof change === "object" &&
          change !== null &&
          typeof (change as Record<string, unknown>).path === "string" &&
          typeof (change as Record<string, unknown>).kind === "string",
      )
      .map((change) => ({
        type: "file_changed" as const,
        path: change.path,
        action: change.kind === "add" ? "create" : change.kind === "delete" ? "delete" : "edit",
      }));
  }

  return [];
}

/**
 * Backward-compatible single-event helper; returns the first `file_changed` event or `null`.
 *
 * Prefer {@link detectFileChanges} for multi-file patches (e.g. `ApplyPatch`).
 *
 * @param toolName - Name of the tool that produced the result.
 * @param result - Raw tool result payload.
 * @returns First detected `file_changed` event, or `null`.
 * @docLink packages/bridge/api-reference#normalizer
 */
export function detectFileChange(toolName: string, result: unknown): NormalizedEvent | null {
  return detectFileChanges(toolName, result)[0] ?? null;
}
